// import { Typography } from '@material-ui/core'
import { PortableWifiOff } from '@material-ui/icons'
import NetworkError from 'Components/NetworkError'
import { API, useDispatch, useSelector } from 'Core'
import { prepareUserObject } from 'Utils/AuthHelpers'
import { debug } from 'Utils/DebugHelper'
import { loginUser, setWhoAmI } from 'infra/redux/actions'
import { accountListSlice, userpreferencesSlice } from 'infra/redux/reducers'
import {
  fetchAllCloudResources,
  rehydrateCloudResourcesFromCache
} from 'features/clouds/utils/CloudResourcesSlice'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import useNetworkPolling from '../useNetworkPolling'
import { Typography } from 'procyon-ui'
import { getSessionToken, setSessionToken } from 'API_LAYER/sessionToken'

export const BootContext = React.createContext({ bootComplete: false })

const ignoredNetworkCheckRoutes = ['/ui/mfa']
const ignoredHistoryRoutes = ['/ui/register', '/ui/webauthn', '/ui/mfa']

function BootProvider(props) {
  const dispatch = useDispatch()
  const history = useHistory()
  // @ts-ignore
  const user = useSelector((state) => state.user)
  const [bootComplete, setBootComplete] = useState(false)
  const fetched = useRef(false)
  const currentUrl = window.location.pathname

  const isNetworkCheckIgnored = ignoredNetworkCheckRoutes.some((route) =>
    currentUrl.includes(route)
  )
  const handleAgentContainer = useCallback(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const isAgentContainer = params.get('agent')
      if (isAgentContainer) {
        debug.setContainer('agent')
      } else {
        debug.setContainer('browser')
      }
    } catch (error) {
      console.error('error in handleAgentContainer', error)
      debug.setContainer('browser')
    }
  }, [])

  const init = useCallback(async () => {
    /** Set current container type from url to localstorage (browser|agent)    */
    handleAgentContainer()
    const ignoreCurrentRoute = ignoredHistoryRoutes.some((route) => currentUrl.includes(route))
    /** Initiate login process */
    const isRedirectIgnoredURL = ['/signupverify', '/signup', '/webauthn', '/mfa'].some((e) =>
      currentUrl.includes(e)
    )
    try {
      // ignore the auth check if on these pages
      if (!['/register', '/mfa'].some((e) => currentUrl.includes(e))) {
        /** Makes api call to get session token */
        await setSessionToken()
        console.log('Session token set', getSessionToken())
        /** Makes api call to get currenly logged in user's DB Object  */
        const response = await API.checkAuth()
        if (response && response.data) {
          const user = prepareUserObject(response)
          dispatch(loginUser({ ...user }))
          const token = response.headers?.['x-auth-token']
          if (token) dispatch(setWhoAmI(JSON.parse(token)))
          if (isRedirectIgnoredURL && !ignoreCurrentRoute) history.push('/')
          return
        }
      }
      /** After all the logic, send to signup. The user is not authenticated */
      // if (!isRedirectIgnoredURL && !ignoreCurrentRoute) {
      //   history.push('/signup')
      // }

      if (!isRedirectIgnoredURL && !ignoreCurrentRoute) {
        window.location.href =
          'https://www.pingidentity.com/en/capability/jit-privileged-access.html'
      }
    } catch (error) {
      /** Redirect user to sign up app in case of a failure in login process */
      // !ignoreCurrentRoute && history.push('/signup')
      if (!ignoreCurrentRoute) {
        window.location.href =
          'https://www.pingidentity.com/en/capability/jit-privileged-access.html'
      }
    } finally {
    }
  }, [handleAgentContainer])

  const loadInitialSlices = async () => {
    // Fetch critical accountList first
    dispatch(accountListSlice.thunk())
    dispatch(userpreferencesSlice.thunk())

    // Rehydrate cloud resources from IndexedDB cache
    await dispatch(rehydrateCloudResourcesFromCache())

    // Trigger background fetch for cloud resources (Smart Sync logic inside thunk will handle caching)
    const cloudTypes = ['AWS', 'GCP', 'AZURE']
    cloudTypes.forEach((cloudType) => {
      dispatch(fetchAllCloudResources({ cloudType }))
    })

    fetched.current = true
    setBootComplete(true)
  }

  /** Initiate login on load */
  useEffect(() => {
    init()
  }, [])

  const options = useMemo(() => {
    return { headers: { Authorization: 'Bearer ' + user.apiKey } }
  }, [user.loggedIn])

  const handleWhoAmIResponse = useCallback(
    (response) => {
      const token = response?.headers?.['x-auth-token']
      if (token) dispatch(setWhoAmI(JSON.parse(token)))
    },
    [dispatch]
  )

  // @ts-ignore
  const { isReachable, startPolling } = useNetworkPolling(
    '/whoami',
    30000,
    options,
    handleWhoAmIResponse
  )

  useEffect(() => {
    if (user.loggedIn) startPolling()
  }, [user.loggedIn])

  /** load required redux states once authenticated */
  useEffect(() => {
    if (user && user.loggedIn && fetched.current === false) loadInitialSlices()
  }, [user])

  return (
    <BootContext.Provider value={{ bootComplete }}>
      {!isNetworkCheckIgnored && (
        <NetworkError
          open={!isReachable}
          Message={
            <Typography
              className='text-center mt-1 text-[var(--color-font-secondary)]'
              variant='body-small'
            >
              Something went wrong can't connect to the server.
              <br />
              <br />
              Please try reloading the application.
            </Typography>
          }
          Action={
            <a
              className='rounded-lg px-4 py-2 bg-[var(--color-button-primary-bg)] text-[var(--color-button-primary-text)] text-sm font-semibold shadow-sm hover:opacity-90 transition'
              href='/'
            >
              Reload now
            </a>
          }
          Icon={<PortableWifiOff style={{ color: 'var(--color-red-base)' }} />}
        />
      )}
      {props.children}
    </BootContext.Provider>
  )
}

export default BootProvider
